import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Calendar, CheckCircle, Play, Square, Timer } from 'lucide-react';
import { toast } from 'sonner';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { staffSelfAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MyAttendancePage = () => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [currentShift, setCurrentShift] = useState(null);
  const [stats, setStats] = useState({
    total_days: 0,
    present_days: 0,
    avg_hours: 0,
    this_month_days: 0,
  });
  const [checkingIn, setCheckingIn] = useState(false);

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await staffSelfAPI.getAttendanceHistory({ limit: 30 });
      setAttendance(data.records || []);
      setStats(data.stats || { total_days: 0, present_days: 0, avg_hours: 0, this_month_days: 0 });
      // Check for active shift
      const active = data.records?.find(r => r.status?.toUpperCase() === 'ACTIVE');
      setCurrentShift(active || null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load attendance'));
      console.error('Error fetching attendance:', error);
      setAttendance([]);
      setStats({ total_days: 0, present_days: 0, avg_hours: 0, this_month_days: 0 });
      setCurrentShift(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  const handleCheckIn = async () => {
    try {
      setCheckingIn(true);
      let deviceInfo = {};

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
        });
        deviceInfo = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
      } catch (geoError) {
        console.warn('Geolocation failed:', geoError);
      }

      const { data } = await staffSelfAPI.startShift({ device_info: deviceInfo });
      toast.success('Checked in successfully!');
      setCurrentShift(data);
      fetchAttendance();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check in. Please try again.'));
      console.error('Check-in error:', error);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    if (!currentShift) return;
    try {
      setCheckingIn(true);
      await staffSelfAPI.endShift({ shift_id: currentShift.id });
      toast.success('Checked out successfully!');
      setCurrentShift(null);
      fetchAttendance();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to check out. Please try again.'));
      console.error('Check-out error:', error);
    } finally {
      setCheckingIn(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '---';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const mins = Math.round((end - start) / 60000);
      if (mins < 0) return null;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return `${h}h ${m}m`;
    } catch {
      return null;
    }
  };

  const getStatusBadge = (status) => {
    const upper = (status || '').toUpperCase();
    switch (upper) {
      case 'ACTIVE':
        return { label: 'In Progress', className: 'bg-teal-50 text-teal-700', pulse: true };
      case 'ENDED':
      case 'COMPLETED':
        return { label: 'Completed', className: 'bg-emerald-50 text-emerald-700', pulse: false };
      default:
        return { label: status || '---', className: 'bg-slate-100 text-slate-600', pulse: false };
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="my-attendance-page">
      <ListPageHeader
        title="My Attendance"
        stats={[
          { icon: Calendar, label: 'Total Days', value: stats.total_days || 0, color: 'slate' },
          { icon: CheckCircle, label: 'Present', value: stats.present_days || 0, color: 'emerald' },
          { icon: Timer, label: 'Avg Hours', value: `${stats.avg_hours || 0}h`, color: 'blue' },
          { icon: Clock, label: 'This Month', value: stats.this_month_days || 0, color: 'purple' },
        ]}
        ctaLabel={currentShift ? (checkingIn ? 'Checking out...' : 'Check Out') : (checkingIn ? 'Checking in...' : 'Check In')}
        ctaIcon={currentShift ? Square : Play}
        onCtaClick={currentShift ? handleCheckOut : handleCheckIn}
        ctaVariant={currentShift ? 'destructive' : 'primary'}
      />

      {/* Active Shift Banner */}
      {currentShift && (
        <div className="flex-shrink-0 px-2 pt-2">
          <div className="bg-teal-50 border border-teal-200 rounded-md p-3 flex items-center gap-3">
            <div className="w-3 h-3 bg-teal-500 rounded-full animate-pulse"></div>
            <div>
              <p className="text-[13px] font-medium text-teal-800">Shift Active</p>
              <p className="text-[11px] text-teal-600">Started at {formatTime(currentShift.start_time)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading attendance...</p>
              </div>
            </div>
          ) : attendance.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Calendar className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No attendance records</p>
              <p className="text-[13px] text-slate-400 mt-1">Check in to start tracking your attendance</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                  </colgroup>
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="tl-th tl-th-teal">Date</th>
                      <th className="tl-th tl-th-teal">Check In</th>
                      <th className="tl-th tl-th-teal">Check Out</th>
                      <th className="tl-th tl-th-teal">Duration</th>
                      <th className="tl-th tl-th-teal">Distance</th>
                      <th className="tl-th tl-th-teal">Status</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {attendance.map((record, index) => {
                      const badge = getStatusBadge(record.status);
                      const duration = formatDuration(record.start_time, record.end_time);
                      return (
                        <tr
                          key={record.id}
                          className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                        >
                          <td className="tl-td tl-cell-primary">
                            {formatDate(record.start_time)}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {formatTime(record.start_time)}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {record.end_time ? formatTime(record.end_time) : <span className="tl-cell-empty">---</span>}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {duration || <span className="tl-cell-empty">---</span>}
                          </td>
                          <td className="tl-td tl-cell-regular">
                            {record.total_distance_km ? `${record.total_distance_km} km` : <span className="tl-cell-empty">---</span>}
                          </td>
                          <td className="tl-td">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.className}`}>
                              {badge.pulse && <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>}
                              {badge.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>Showing <span className="font-medium text-slate-700">{attendance.length}</span> records</p>
                <p>Avg Hours: <span className="font-medium text-slate-700">{stats.avg_hours || 0}h</span></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyAttendancePage;
